/**
 * src/domains/staff/api.ts
 *
 * Staff / member management — bounded context for company admin operations.
 *
 * Previously lived in src/domains/orders/api.ts, causing domain leakage:
 * staff management had no relation to work orders, but shared a module with
 * order CRUD, outbox logic, and dispatcher feeds.
 *
 * This module owns:
 *   - Fetching company members (all roles)
 *   - Adding new employees (Edge Function → RPC fallback)
 *   - Updating employee profile (name, phone)
 *   - Changing member role
 *   - Deactivating / reactivating membership (soft delete)
 *   - Local-mode mutation state for demo / sandbox mode
 *
 * fetchTechnicians() stays in orders/api.ts — it is consumed by dispatcher
 * useTeam() and is part of the order assignment flow, not admin staff management.
 */

import type { CompanyRole } from '../companies/types';
import type { CompanyMembership } from '../companies/types';
import type { User } from '../auth/types';
import { isSupabaseConfigured, requireSupabase, supabase } from '../auth/supabaseClient';
import { getInitials } from '../../shared/utils/user';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toPlatformRole(value: unknown): User['platform_role'] {
  return value === 'platform_owner' || value === 'platform_admin' ? value : null;
}

function mapCompanyMemberUser(
  profile: Record<string, unknown>,
  membership: {
    id: string;
    user_id: string;
    company_id: string;
    company_role: CompanyRole;
    is_active: boolean;
    is_default: boolean;
    created_at: string;
  },
): User {
  return {
    id: String(profile.id),
    company_id: membership.company_id,
    company_membership_id: membership.id,
    company_role: membership.company_role,
    email: String(profile.email),
    name: String(profile.name),
    phone: profile.phone ? String(profile.phone) : null,
    platform_role: toPlatformRole(profile.platform_role),
    role: membership.company_role,
    memberships: [membership],
    is_active: Boolean(profile.is_active),
    membership_is_active: membership.is_active,
    initials: getInitials(String(profile.name)),
    created_at: String(profile.created_at ?? new Date().toISOString()),
  };
}

// ─── Local-mode member mutation state (ephemeral, per-session) ────────────────

const localInvited: User[] = [];
const localDeactivatedIds = new Set<string>();
const localRoleOverrides = new Map<string, CompanyRole>();
const LOCAL_STAFF_STATE_KEY = 'fieldsnap_local_staff_state';

function isCompanyRole(value: unknown): value is CompanyRole {
  return value === 'company_admin' || value === 'dispatcher' || value === 'technician';
}

function persistLocalMutationState() {
  try {
    localStorage.setItem(
      LOCAL_STAFF_STATE_KEY,
      JSON.stringify({
        invited: localInvited,
        deactivated: Array.from(localDeactivatedIds),
        roles: Array.from(localRoleOverrides.entries()),
      }),
    );
  } catch {
    // ignore storage errors
  }
}

function loadLocalMutationState() {
  localInvited.splice(0, localInvited.length);
  localDeactivatedIds.clear();
  localRoleOverrides.clear();

  try {
    const raw = localStorage.getItem(LOCAL_STAFF_STATE_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw) as {
      invited?: unknown;
      deactivated?: unknown;
      roles?: unknown;
    };

    if (Array.isArray(parsed.invited)) {
      const invitedById = new Map<string, User>();
      parsed.invited.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        const candidate = entry as Partial<User>;
        if (
          typeof candidate.id !== 'string' ||
          typeof candidate.company_id !== 'string' ||
          typeof candidate.company_membership_id !== 'string' ||
          !isCompanyRole(candidate.company_role)
        ) {
          return;
        }
        invitedById.set(candidate.id, candidate as User);
      });
      localInvited.push(...invitedById.values());
    }

    if (Array.isArray(parsed.deactivated)) {
      parsed.deactivated
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
        .forEach((id) => localDeactivatedIds.add(id));
    }

    if (Array.isArray(parsed.roles)) {
      parsed.roles.forEach((entry) => {
        if (
          Array.isArray(entry) &&
          entry.length === 2 &&
          typeof entry[0] === 'string' &&
          isCompanyRole(entry[1])
        ) {
          localRoleOverrides.set(entry[0], entry[1]);
        }
      });
    }
  } catch {
    // ignore parse errors
  }
}

loadLocalMutationState();

function applyLocalMutations(members: User[]): User[] {
  let result = members.filter((m) => !localDeactivatedIds.has(m.company_membership_id));
  result = result.map((m) => {
    const override = localRoleOverrides.get(m.id);
    if (override && override !== m.company_role) {
      return { ...m, company_role: override, role: override };
    }
    return m;
  });
  const invitedForCompany = localInvited.filter((m) => m.company_id === (members[0]?.company_id ?? m.company_id));
  const existingIds = new Set(result.map((m) => m.id));
  invitedForCompany.forEach((m) => {
    if (!existingIds.has(m.id)) result.push(m);
  });
  return result;
}

// ─── Fetch members ────────────────────────────────────────────────────────────

async function fetchCompanyMembers(companyId: string, role?: CompanyRole, includeInactive?: boolean): Promise<User[]> {
  if (!isSupabaseConfigured) {
    const now = new Date().toISOString();
    const localUsers: User[] = [
      {
        id: 'local-company-admin',
        company_id: companyId,
        company_membership_id: 'local-membership-company-admin',
        company_role: 'company_admin',
        email: 'admin@fieldsnap.app',
        name: 'Локальный администратор',
        phone: '+7 900 000-00-10',
        platform_role: null,
        role: 'company_admin',
        memberships: [
          {
            id: 'local-membership-company-admin',
            user_id: 'local-company-admin',
            company_id: companyId,
            company_role: 'company_admin',
            is_active: true,
            is_default: true,
            created_at: now,
          },
        ],
        is_active: true,
        membership_is_active: true,
        initials: getInitials('Локальный администратор'),
        created_at: now,
      },
      {
        id: 'local-dispatcher',
        company_id: companyId,
        company_membership_id: 'local-membership-dispatcher',
        company_role: 'dispatcher',
        email: 'disp@fieldsnap.app',
        name: 'Локальный диспетчер',
        phone: '+7 900 000-00-09',
        platform_role: null,
        role: 'dispatcher',
        memberships: [
          {
            id: 'local-membership-dispatcher',
            user_id: 'local-dispatcher',
            company_id: companyId,
            company_role: 'dispatcher',
            is_active: true,
            is_default: true,
            created_at: now,
          },
        ],
        is_active: true,
        membership_is_active: true,
        initials: getInitials('Локальный диспетчер'),
        created_at: now,
      },
      {
        id: 'local-technician',
        company_id: companyId,
        company_membership_id: 'local-membership-technician',
        company_role: 'technician',
        email: 'tech@fieldsnap.app',
        name: 'Локальный техник',
        phone: '+7 900 000-00-11',
        platform_role: null,
        role: 'technician',
        memberships: [
          {
            id: 'local-membership-technician',
            user_id: 'local-technician',
            company_id: companyId,
            company_role: 'technician',
            is_active: true,
            is_default: true,
            created_at: now,
          },
        ],
        is_active: true,
        membership_is_active: true,
        initials: getInitials('Локальный техник'),
        created_at: now,
      },
      {
        id: 'local-tech-2',
        company_id: companyId,
        company_membership_id: 'local-membership-tech-2',
        company_role: 'technician',
        email: 'tech2@fieldsnap.app',
        name: 'Техник 2',
        phone: '+7 900 000-00-12',
        platform_role: null,
        role: 'technician',
        memberships: [
          {
            id: 'local-membership-tech-2',
            user_id: 'local-tech-2',
            company_id: companyId,
            company_role: 'technician',
            is_active: true,
            is_default: true,
            created_at: now,
          },
        ],
        is_active: true,
        membership_is_active: true,
        initials: getInitials('Техник 2'),
        created_at: now,
      },
    ];

    return applyLocalMutations(role ? localUsers.filter((user) => user.company_role === role) : localUsers);
  }

  let membershipQuery = requireSupabase()
    .from('company_memberships')
    .select('*')
    .eq('company_id', companyId)
    .order('is_default', { ascending: false })
    .order('created_at', { ascending: true });

  if (!includeInactive) {
    membershipQuery = membershipQuery.eq('is_active', true);
  }

  if (role) {
    membershipQuery = membershipQuery.eq('company_role', role);
  }
  const { data: membershipRows, error: membershipError } = await membershipQuery;
  if (membershipError) throw membershipError;

  const memberships = (membershipRows ?? []).map((row) => ({
    id: String(row.id),
    user_id: String(row.user_id),
    company_id: String(row.company_id),
    company_role:
      row.company_role === 'company_admin' || row.company_role === 'admin' || row.company_role === 'manager'
        ? 'company_admin'
        : row.company_role === 'dispatcher' || row.company_role === 'technician'
          ? row.company_role
          : 'technician',
    is_active: Boolean(row.is_active ?? true),
    is_default: Boolean(row.is_default ?? false),
    created_at: String(row.created_at ?? new Date().toISOString()),
  }));

  const scopedMemberships = memberships.filter(
    (membership) => membership.company_id === companyId,
  );

  if (scopedMemberships.length === 0) return [];

  const userIds = [...new Set(scopedMemberships.map((membership) => membership.user_id))];
  const { data: profileRows, error: profileError } = await requireSupabase()
    .from('user_profiles')
    .select('id, email, name, phone, platform_role, is_active, created_at')
    .in('id', userIds)
    .order('name');
  if (profileError) throw profileError;

  const membershipByUserId = new Map(scopedMemberships.map((membership) => [membership.user_id, membership]));

  return (profileRows ?? [])
    .map((row) => {
      const membership = membershipByUserId.get(String(row.id));
      if (!membership) return null;
      return mapCompanyMemberUser(row as Record<string, unknown>, membership as {
        id: string;
        user_id: string;
        company_id: string;
        company_role: CompanyRole;
        is_active: boolean;
        is_default: boolean;
        created_at: string;
      });
    })
    .filter((user): user is User => user !== null);
}

export async function fetchAllMembers(companyId: string, includeInactive = true): Promise<User[]> {
  return fetchCompanyMembers(companyId, undefined, includeInactive);
}

// ─── Add employee (Edge Function → RPC fallback) ──────────────────────────────

export interface AddEmployeeInput {
  email: string;
  password: string;
  name: string;
  phone?: string;
  role: CompanyRole;
}

function mapAddEmployeeResult(result: {
  userId: string;
  email: string;
  name: string;
  membershipId: string;
  companyRole: string;
  isExistingUser: boolean;
}, companyId: string): User {
  const membership: CompanyMembership = {
    id: result.membershipId,
    user_id: result.userId,
    company_id: companyId,
    company_role: result.companyRole as CompanyRole,
    is_active: true,
    is_default: true,
    created_at: new Date().toISOString(),
  };
  return {
    id: result.userId,
    company_id: companyId,
    company_membership_id: result.membershipId,
    company_role: result.companyRole as CompanyRole,
    email: result.email,
    name: result.name,
    phone: null,
    platform_role: null,
    role: result.companyRole as CompanyRole,
    memberships: [membership],
    is_active: true,
    membership_is_active: true,
    initials: getInitials(result.name),
    created_at: new Date().toISOString(),
  };
}

export async function addEmployee(companyId: string, payload: AddEmployeeInput): Promise<User> {
  const email = payload.email.toLowerCase().trim();
  const name = payload.name.trim();
  const phone = payload.phone?.trim() || null;
  const role = payload.role;

  if (!payload.password || payload.password.length < 8) {
    throw new Error('Пароль должен содержать минимум 8 символов');
  }

  if (!isSupabaseConfigured) {
    const id = `local-invited-${Date.now()}`;
    const now = new Date().toISOString();
    const user: User = {
      id,
      company_id: companyId,
      company_membership_id: `local-membership-${id}`,
      company_role: role,
      email,
      name,
      phone,
      platform_role: null,
      role,
      memberships: [
        {
          id: `local-membership-${id}`,
          user_id: id,
          company_id: companyId,
          company_role: role,
          is_active: true,
          is_default: true,
          created_at: now,
        },
      ],
      is_active: true,
      membership_is_active: true,
      initials: getInitials(name),
      created_at: now,
    };
    localInvited.push(user);
    persistLocalMutationState();
    return user;
  }

  function logError(label: string, err: unknown, extra?: Record<string, unknown>): void {
    const base: Record<string, unknown> = {
      message: err instanceof Error ? err.message : String(err),
      name: err instanceof Error ? err.name : typeof err,
      stack: err instanceof Error ? err.stack : undefined,
    };

    if (err !== null && typeof err === 'object') {
      const e = err as Record<string, unknown>;
      if (e['code'] !== undefined) base['code'] = e['code'];
      if (e['details'] !== undefined) base['details'] = e['details'];
      if (e['hint'] !== undefined) base['hint'] = e['hint'];
      if (e['context'] !== undefined) base['context'] = e['context'];
      if (e['status'] !== undefined) base['status'] = e['status'];
      if (e['body'] !== undefined) base['body'] = e['body'];
    }

    console.error(`[addEmployee] ${label}`, { ...base, ...extra });
  }

  try {
    const sb = requireSupabase();
    const { data, error } = await sb.functions.invoke('create-employee', {
      body: {
        companyId,
        email,
        password: payload.password,
        name,
        phone,
        companyRole: role,
      },
    });

    if (error) {
      // FunctionsFetchError wraps the real network/CORS error in .context.
      // Extract and surface it so the developer can see the actual failure cause.
      const isFetchError =
        error instanceof Error &&
        (error.name === 'FunctionsFetchError' || error.message === 'Failed to send a request to the Edge Function');
      if (isFetchError) {
        const ctx = (error as unknown as { context?: unknown }).context;
        const ctxMsg = ctx instanceof Error ? ctx.message : String(ctx ?? '');
        const hint =
          ctxMsg.includes('Failed to fetch') || ctxMsg.includes('NetworkError')
            ? 'Проверьте, задеплоена ли edge function create-employee (supabase functions deploy create-employee --no-verify-jwt) и открыты ли CORS-заголовки.'
            : ctxMsg;
        throw new Error(`Сетевая ошибка при вызове edge function. ${hint}`);
      }
      throw error;
    }

    const result = data as {
      userId: string;
      email: string;
      name: string;
      membershipId: string;
      companyRole: string;
      isExistingUser: boolean;
      functionVersion?: string;
      warning?: string;
      warningMessage?: string;
    };

    if (!result?.functionVersion) {
      throw new Error('OUTDATED_CREATE_EMPLOYEE_FUNCTION');
    }

    const user = mapAddEmployeeResult(result, companyId);

    // Surface the password-unchanged warning as a property so the UI can inform
    // the admin without throwing — the membership was successfully created.
    if (result.warning === 'existing_user_password_unchanged') {
      (user as User & { _warning?: string })._warning = result.warningMessage ?? result.warning;
    }

    return user;
  } catch (err) {
    logError('create-employee failed', err, {
      context: { companyId, email, name, role },
    });

    const message =
      err instanceof Error
        ? err.message
        : typeof err === 'string'
          ? err
          : '';

    if (message.includes('OUTDATED_CREATE_EMPLOYEE_FUNCTION')) {
      throw new Error('На сервере задеплоена устаревшая create-employee function. Нужен redeploy.');
    }

    if (message.includes('already exists') || message.includes('unique_violation')) {
      throw new Error('Пользователь с таким email уже существует');
    }

    if (message.includes('insufficient_privilege') || message.includes('Forbidden')) {
      throw new Error('Недостаточно прав для добавления сотрудника');
    }

    if (message.includes('Достигнут лимит пользователей по тарифу')) {
      throw new Error('Достигнут лимит пользователей по текущему тарифу');
    }

    if (message.includes('подписка компании неактивна') || message.includes('нет активированной подписки')) {
      throw new Error('Добавление сотрудников недоступно: подписка неактивна');
    }

    if (message.includes('password must be at least 8 characters')) {
      throw new Error('Пароль должен содержать минимум 8 символов');
    }

    if (message.includes('email is not valid')) {
      throw new Error('Введите корректный email');
    }

    if (message.includes('companyRole must be')) {
      throw new Error('Некорректная роль сотрудника');
    }

    throw new Error(message || 'Не удалось создать сотрудника');
  }
}

// ─── Update employee profile (name + phone) ───────────────────────────────────

export async function updateEmployeeProfile(
  userId: string,
  fields: { name?: string; phone?: string | null },
): Promise<void> {
  if (!isSupabaseConfigured) {
    // In demo/local mode: apply the update in-memory so the UI reflects the
    // change within the session. No Supabase means no persistent profile store,
    // so the change is intentionally ephemeral.
    const invited = localInvited.find((m) => m.id === userId);
    if (invited) {
      if (fields.name !== undefined) invited.name = fields.name.trim();
      if (fields.phone !== undefined) invited.phone = fields.phone?.trim() || null;
      persistLocalMutationState();
    }
    // For non-invited local users the update is a no-op — those are seed fixtures
    // and are not expected to be edited in demo mode.
    return;
  }

  const patch: Record<string, unknown> = {};
  if (fields.name !== undefined) patch.name = fields.name.trim();
  if (fields.phone !== undefined) patch.phone = fields.phone?.trim() || null;
  if (Object.keys(patch).length === 0) return;

  patch.updated_at = new Date().toISOString();

  const { error } = await requireSupabase().from('user_profiles').update(patch).eq('id', userId);
  if (error) {
    if (error.message?.includes('insufficient_privilege')) {
      throw new Error('Недостаточно прав для редактирования профиля');
    }
    throw new Error(error.message ?? 'Не удалось обновить профиль');
  }
}

// ─── Update member role ───────────────────────────────────────────────────────

export async function updateMemberRole(membershipId: string, role: CompanyRole): Promise<void> {
  if (!isSupabaseConfigured) {
    localRoleOverrides.set(membershipId, role);
    persistLocalMutationState();
    return;
  }

  // Uses the safe_change_member_role RPC which enforces server-side invariants:
  //   1. Caller cannot change their own role via this path.
  //   2. Cannot demote the last active company_admin.
  const { data, error } = await requireSupabase()
    .rpc('safe_change_member_role', { p_membership_id: membershipId, p_new_role: role });

  if (error) {
    if (error.message?.includes('insufficient_privilege')) {
      throw new Error('Недостаточно прав для изменения роли');
    }
    throw new Error(error.message ?? 'Не удалось обновить роль');
  }

  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.error) {
    throw new Error(result.error);
  }
}

// ─── Deactivate member (soft delete on membership) ────────────────────────────
// Uses the safe_deactivate_member RPC which enforces server-side invariants:
//   1. Caller cannot deactivate themselves.
//   2. Cannot remove the last active company_admin.
// These checks are enforced at DB level so they cannot be bypassed by a
// direct API call even if the client-side guards are removed.

export async function deactivateMember(membershipId: string): Promise<void> {
  if (!isSupabaseConfigured) {
    localDeactivatedIds.add(membershipId);
    persistLocalMutationState();
    return;
  }

  const { data, error } = await requireSupabase()
    .rpc('safe_deactivate_member', { p_membership_id: membershipId });

  if (error) {
    if (error.message?.includes('insufficient_privilege')) {
      throw new Error('Недостаточно прав для деактивации');
    }
    throw new Error(error.message ?? 'Не удалось деактивировать сотрудника');
  }

  // RPC returns {ok: true} on success or {error: "..."} on business-logic violation
  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.error) {
    throw new Error(result.error);
  }
}

// ─── Reactivate member ────────────────────────────────────────────────────────

export async function reactivateMember(membershipId: string): Promise<void> {
  if (!isSupabaseConfigured) {
    localDeactivatedIds.delete(membershipId);
    persistLocalMutationState();
    return;
  }

  const { data, error } = await requireSupabase()
    .rpc('safe_reactivate_member', { p_membership_id: membershipId });

  if (error) {
    if (error.message?.includes('insufficient_privilege')) {
      throw new Error('Недостаточно прав для активации');
    }
    throw new Error(error.message ?? 'Не удалось активировать сотрудника');
  }

  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.error) {
    throw new Error(result.error);
  }
}
